import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import CheckersBoard from './CheckersBoard';
import CheckersSettings from './CheckersSettings';
import {
  createInitialGameState,
  isValidMove,
  makeMove,
  getPossibleMoves,
} from './logic';
import {
  subscribeToGame,
  updateGameState,
  updateGameStatus,
  requestRematch,
  resetGameForRematch,
  saveGameToHistory,
  updateMatchStats,
} from '../../services/gameService';
import { addRecentOpponent } from '../../services/friendService';
import { getCheckersPreferences, getBoardColors } from '../../services/checkersPreferencesService';
import { database } from '../../services/firebase';
import { ref, update, serverTimestamp } from 'firebase/database';
import { safeString } from '../../utils/safeString';
import './Checkers.css';

const Checkers = ({ roomCode, roomData, user, variant = 'american' }) => {
  const navigate = useNavigate();
  const [gameState, setGameState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [legalMoves, setLegalMoves] = useState([]);
  const [rematchRequested, setRematchRequested] = useState(false);
  const [opponentRematchRequested, setOpponentRematchRequested] = useState(false);
  const [error, setError] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [checkersPreferences, setCheckersPreferences] = useState({
    boardStyle: 'green',
  });
  const confettiShownRef = useRef(false);
  const gameRecordedRef = useRef(false);

  // Game title and type based on variant
  const gameTitle = variant === 'russian' ? 'Russian Checkers' : 'American Checkers';
  const gameType = variant === 'russian' ? 'russiancheckers' : 'americancheckers';
  const gameIcon = variant === 'russian' ? '⚫ Russian Checkers ⚪' : '🔴 American Checkers ⚫';

  // Validate props
  if (!roomCode || !roomData || !user) {
    return (
      <div className="checkers-container">
        <div className="error-message">Missing required data. Please try again.</div>
      </div>
    );
  }

  const isHost = roomData.host && roomData.host.userId === user.uid;
  const opponent = isHost ? roomData.guest : roomData.host;

  // If opponent left, show waiting state
  if (!opponent) {
    const handleReturnToLobby = async () => {
      try {
        const roomRef = ref(database, `rooms/${roomCode}`);
        await update(roomRef, {
          status: 'waiting',
          gameSelected: null,
          lastActivity: serverTimestamp()
        });
        navigate(`/room/${roomCode}`);
      } catch (error) {
        console.error('Error returning to lobby:', error);
        navigate(`/room/${roomCode}`);
      }
    };

    return (
      <div className="checkers-container">
        <div className="game-header">
          <h2>{gameTitle}</h2>
        </div>
        <div className="player-waiting-state">
          <div className="waiting-spinner"></div>
          <h3>Player left the room</h3>
          <p>Waiting for player to return...</p>
          <p className="waiting-hint">The game will resume when they come back</p>
          <button className="leave-button" onClick={handleReturnToLobby}>
            Return to Lobby
          </button>
        </div>
      </div>
    );
  }

  const myColor = gameState ? (isHost ? gameState.hostColor : gameState.guestColor) : null;
  const opponentColor = myColor === 'black' ? 'red' : 'black';
  const isMyTurn = gameState && gameState.currentTurn === myColor;

  // Load checkers preferences
  useEffect(() => {
    if (user) {
      loadPreferences();
    }
  }, [user]);

  const loadPreferences = async () => {
    const prefs = await getCheckersPreferences(user.uid);
    setCheckersPreferences(prefs);
  };

  // Subscribe to game updates
  useEffect(() => {
    try {
      const unsubscribe = subscribeToGame(roomCode, async (game) => {
        if (game && game.gameState) {
          setGameState(game.gameState);

          // Check rematch requests
          if (game.rematchRequests) {
            const myRequest = !!game.rematchRequests[user.uid];
            const opponentRequest = !!game.rematchRequests[opponent.userId];

            setRematchRequested(myRequest);
            setOpponentRematchRequested(opponentRequest);

            // If both players requested rematch, only the host resets the game
            if (myRequest && opponentRequest && isHost && game.gameState.isGameOver) {
              const newGameState = createInitialGameState(
                roomData.host.userId,
                roomData.guest.userId,
                variant
              );

              await resetGameForRematch(
                roomCode,
                gameType,
                newGameState,
                {
                  host: roomData.host,
                  guest: roomData.guest,
                }
              );
            }
          } else {
            setRematchRequested(false);
            setOpponentRematchRequested(false);
          }
        } else if (game === null) {
          setError('Game not found. It may not have been created yet.');
        } else if (game && !game.gameState) {
          setError('Game data is missing gameState. Please try restarting the game.');
        }

        setLoading(false);
      });

      return () => unsubscribe();
    } catch (err) {
      console.error('Error setting up game subscription:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [roomCode, user.uid, opponent.userId, isHost, roomData]);

  // Trigger confetti on win
  useEffect(() => {
    if (gameState && gameState.isGameOver && !confettiShownRef.current) {
      const iWon = gameState.winner === myColor;

      if (iWon) {
        confetti({
          particleCount: 150,
          spread: 80,
          origin: { y: 0.6 },
        });
        confettiShownRef.current = true;
      }
    }

    // Reset flags when game restarts
    if (gameState && !gameState.isGameOver) {
      confettiShownRef.current = false;
      gameRecordedRef.current = false;
    }
  }, [gameState, myColor]);

  // Save game to history when it ends
  useEffect(() => {
    if (gameState && gameState.isGameOver && !gameRecordedRef.current) {
      gameRecordedRef.current = true;

      const iWon = gameState.winner === myColor;
      const result = iWon ? 'won' : 'lost';

      // Convert winner color to user ID
      const winnerUserId = gameState.winner === myColor ? user.uid : opponent.userId;

      saveGameToHistory(user.uid, {
        gameType: gameType,
        opponent: safeString(opponent?.displayName, 'Opponent'),
        opponentId: opponent.userId,
        result,
        winner: winnerUserId,
        roomCode,
      });

      updateMatchStats(user.uid, gameType, result);

      addRecentOpponent(
        user.uid,
        opponent.userId,
        safeString(opponent?.displayName, 'Opponent'),
        safeString(opponent?.photoURL, ''),
        gameType,
        result
      );

      updateGameStatus(roomCode, 'completed');
    }
  }, [gameState, myColor, user.uid, opponent, roomCode]);

  const handleSquareClick = async (row, col) => {
    if (!gameState || gameState.isGameOver || !isMyTurn) return;

    // Safety check for board position
    if (!gameState.board[row] || gameState.board[row][col] === undefined) return;

    const piece = gameState.board[row][col];

    // If a piece is already selected
    if (selectedSquare) {
      const fromRow = selectedSquare.row;
      const fromCol = selectedSquare.col;

      // If clicking on the same square, deselect
      if (fromRow === row && fromCol === col) {
        setSelectedSquare(null);
        setLegalMoves([]);
        return;
      }

      // Try to make the move
      if (isValidMove(gameState, fromRow, fromCol, row, col, variant)) {
        const newGameState = makeMove(gameState, fromRow, fromCol, row, col, variant);
        await updateGameState(roomCode, newGameState);

        setSelectedSquare(null);
        setLegalMoves([]);
      } else {
        // Invalid move, try selecting a new piece
        if (piece !== 0) {
          const pieceType = piece === 1 || piece === 2 ? 'black' : 'red';
          if (pieceType === myColor) {
            setSelectedSquare({ row, col });
            setLegalMoves(getPossibleMoves(gameState.board, row, col, false, variant));
          }
        } else {
          setSelectedSquare(null);
          setLegalMoves([]);
        }
      }
    } else {
      // No piece selected, try to select one
      if (piece !== 0) {
        const pieceType = piece === 1 || piece === 2 ? 'black' : 'red';
        if (pieceType === myColor) {
          setSelectedSquare({ row, col });
          setLegalMoves(getPossibleMoves(gameState.board, row, col, false, variant));
        }
      }
    }
  };

  const handleRematch = async () => {
    await requestRematch(roomCode, user.uid);
  };

  const handleLeaveGame = async () => {
    try {
      const roomRef = ref(database, `rooms/${roomCode}`);
      await update(roomRef, {
        status: 'waiting',
        gameSelected: null,
        'host/gameSelection': null,
        'guest/gameSelection': null,
        lastActivity: serverTimestamp()
      });
      navigate(`/room/${roomCode}`);
    } catch (error) {
      console.error('Error leaving game:', error);
      navigate(`/room/${roomCode}`);
    }
  };

  if (loading) {
    return (
      <div className="checkers-container">
        <div className="loading-message">
          <div className="spinner-large"></div>
          <p>Loading game...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="checkers-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!gameState) {
    return (
      <div className="checkers-container">
        <div className="loading-message">Waiting for game to start...</div>
      </div>
    );
  }

  const lastMove = gameState.moveHistory && gameState.moveHistory.length > 0
    ? gameState.moveHistory[gameState.moveHistory.length - 1]
    : null;

  const iWon = gameState.isGameOver && gameState.winner === myColor;
  const iLost = gameState.isGameOver && gameState.winner !== myColor;

  const myPieces = myColor === 'black' ? gameState.blackPiecesRemaining : gameState.redPiecesRemaining;
  const opponentPieces = myColor === 'black' ? gameState.redPiecesRemaining : gameState.blackPiecesRemaining;

  return (
    <div className="checkers-container">
      {/* Header */}
      <div className="game-header">
        <h1 className="game-title">{gameIcon}</h1>
        <button
          className="checkers-settings-button"
          onClick={() => setShowSettings(true)}
          title="Customize Board"
        >
          ⚙️
        </button>
      </div>

      {/* Game Info */}
      <div className="game-info">
        <div className="players-section">
          {/* Opponent */}
          <div className={`player-info opponent ${gameState.currentTurn === opponentColor ? 'active-player' : ''}`}>
            <img
              src={safeString(opponent?.photoURL, '') || '/default-avatar.png'}
              alt={safeString(opponent?.displayName, 'Opponent')}
              className="player-avatar"
            />
            <div className="player-details">
              <span className="player-name">{safeString(opponent?.displayName, 'Opponent')}</span>
              <span className="player-color">{opponentColor === 'black' ? '⚫ Black' : '🔴 Red'}</span>
              <span className="pieces-count">{opponentPieces} pieces</span>
            </div>
          </div>

          {/* Current Player */}
          <div className={`player-info current ${gameState.currentTurn === myColor ? 'active-player' : ''}`}>
            <img
              src={safeString(user?.photoURL, '') || '/default-avatar.png'}
              alt={safeString(user?.displayName, 'You')}
              className="player-avatar"
            />
            <div className="player-details">
              <span className="player-name">You</span>
              <span className="player-color">{myColor === 'black' ? '⚫ Black' : '🔴 Red'}</span>
              <span className="pieces-count">{myPieces} pieces</span>
            </div>
          </div>
        </div>
      </div>

      {/* Turn Indicator */}
      {!gameState.isGameOver && (
        <div className="turn-indicator-section">
          <p className="turn-indicator">
            {isMyTurn ? "Your Turn!" : `${safeString(opponent?.displayName, 'Opponent')}'s Turn`}
          </p>
        </div>
      )}

      {/* Game Status */}
      {!gameState.isGameOver && (
        <div className="game-status">
          <p className="status-text">
            {isMyTurn ? "Select a piece to move" : `Waiting for ${safeString(opponent?.displayName, 'Opponent')}...`}
          </p>
        </div>
      )}

      {/* Checkers Board */}
      <CheckersBoard
        board={gameState.board}
        selectedSquare={selectedSquare}
        legalMoves={legalMoves}
        lastMove={lastMove}
        onSquareClick={handleSquareClick}
        disabled={!isMyTurn || gameState.isGameOver}
        myColor={myColor}
        boardColors={getBoardColors(checkersPreferences.boardStyle)}
      />

      {/* Game Over Message */}
      {gameState.isGameOver && (
        <div className="game-over">
          <div className={`result-message ${iWon ? 'won' : 'lost'}`}>
            {iWon ? (
              <>
                <span className="result-icon">🎉</span>
                <h2>You Won!</h2>
                <p>Congratulations! All opponent pieces captured or blocked!</p>
              </>
            ) : (
              <>
                <span className="result-icon">😞</span>
                <h2>You Lost</h2>
                <p>Better luck next time!</p>
              </>
            )}
          </div>

          {/* Rematch Section */}
          <div className="rematch-section">
            {!rematchRequested && !opponentRematchRequested && (
              <button className="rematch-button" onClick={handleRematch}>
                🔄 Request Rematch
              </button>
            )}

            {rematchRequested && !opponentRematchRequested && (
              <button className="rematch-button waiting" disabled>
                ⏳ Waiting for opponent...
              </button>
            )}

            {opponentRematchRequested && !rematchRequested && (
              <button className="rematch-button accept" onClick={handleRematch}>
                ✓ Accept Rematch
              </button>
            )}

            {rematchRequested && opponentRematchRequested && (
              <button className="rematch-button starting" disabled>
                🎮 Starting rematch...
              </button>
            )}

            {opponentRematchRequested && !rematchRequested && (
              <p className="rematch-hint">Opponent wants a rematch!</p>
            )}
          </div>
        </div>
      )}

      {/* Leave Game Button */}
      <div className="leave-game-section">
        <button className="leave-button" onClick={handleLeaveGame}>
          Leave Game
        </button>
      </div>

      {/* Checkers Settings Dialog */}
      {showSettings && (
        <CheckersSettings
          user={user}
          onClose={() => setShowSettings(false)}
          onPreferencesChange={(prefs) => setCheckersPreferences(prefs)}
        />
      )}
    </div>
  );
};

export default Checkers;
